import { RenderTreeNodePayload, Group, ActionIcon, Tooltip, TreeNodeData, Checkbox } from '@mantine/core';
import { IconArrowsMaximize, IconChevronDown } from '@tabler/icons-react';
import { useEffect, useRef, memo, useCallback, useMemo } from 'react';
import useStore from '../../data/store';
import useTransientStore from '../../data/transcientStore';
import { useTranslation } from "../../hooks/useTranslation";

interface LocationLeafProps extends RenderTreeNodePayload {
    onUpdate: (nodeValue: TreeNodeData) => void;
    openModal: (object: TreeNodeData) => void;
}

const LocationLeaf = memo(function LocationLeaf({
    node,
    expanded,
    hasChildren,
    elementProps,
    tree,
    onUpdate,
    openModal
}: LocationLeafProps) {
    //#region Properties

    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 328, addressSystemId: addrIdFromStore || 0, autoLoad: true });

    const clickedPolygonIDs = useStore((state) => state.clickedPolygonIDs);
    const setClickedPolygonIDs = useStore((state) => state.setClickedPolygonIDs);
    const setUserGeometriesVisible = useTransientStore((state) => state.setUserGeometriesVisible);
    const isHighlighted = clickedPolygonIDs.includes(node.nodeProps?.object?.LocalID) ? true : false;
    const expandButtonVisible = node?.value.includes("rims-preset-node") ? false : true;
	
	// Check if this is a status node to display color indicator
	const isStatusNode = node?.value.includes("rims-preset-node-status");
	const statusColor = node.nodeProps?.statusColor;

	// Only show checkbox for top-level parent nodes
	const isTopLevelParent = node.value === "rims-preset-node-user-geometries" || 
							 node.value === "rims-preset-node-static-layer";

	// Checked state
	const checked = tree.isNodeChecked(node.value);
	const indeterminate = tree.isNodeIndeterminate(node.value);

	// Memoize style object to prevent recreation
	const groupStyle = useMemo(() => ({
		backgroundColor: isHighlighted ? 'var(--mantine-color-green-light)' : 
						(hasChildren && expanded) ? 'var(--mantine-color-blue-light)' : 'transparent',
		borderRadius: (isHighlighted || (hasChildren && expanded)) ? '4px' : '0', 
		cursor: 'pointer',
		transition: 'background-color 0.15s ease-in-out',
		padding: '1px 2px', 
		margin: '1px 0',
		...((!isHighlighted && !(hasChildren && expanded)) && {
			':hover': {
				backgroundColor: 'var(--mantine-color-gray-0)'
			}
		})
	}), [isHighlighted, hasChildren, expanded]);

    //#endregion

    //#region Hooks

    const nodeRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isHighlighted && nodeRef.current) {
            nodeRef.current.scrollIntoView({
                behavior: 'smooth', // Animates the scrolling
                block: 'center' // Ensures the node is centred in the viewport
            });
        }
    }, [isHighlighted]);

    //#endregion

    //#region Functions

	/**
	 * Handles checkbox click to check/uncheck nodes
	 */
	const handleCheckboxClick = useCallback((event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
		event.stopPropagation();
		const newCheckedState = !checked;
		
		if (newCheckedState) {
			tree.checkNode(node.value);
		} else {
			tree.uncheckNode(node.value);
		}
		
		// Update the store if this is the User Geometries node
		if (node.value === 'rims-preset-node-user-geometries') {
			setUserGeometriesVisible(newCheckedState);
		}
	}, [checked, tree, node.value, setUserGeometriesVisible]);

    /**
     * Handles the click event for visibility.
     *
     * @param event - The click event.
     */
    const handleVisibilityClick = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();

        setClickedPolygonIDs([]); //Clear the clicked polygon IDs as we don't want to highlight any of the previously clicked nodes

        node.nodeProps = { ...node.nodeProps, visible: true };

        function adjustChildrenVisibility(nodeObject: TreeNodeData) {
            if (nodeObject.children) {
                nodeObject.children.forEach((child: TreeNodeData) => {
                    child.nodeProps = { ...child.nodeProps, visible: true };
                    adjustChildrenVisibility(child);
                });
            }
        }

        adjustChildrenVisibility(node);

        onUpdate(node); //Update the node with visibility changes
    }, [node, onUpdate, setClickedPolygonIDs]);

    /**
     * Handles the event when the details button is clicked.
     *
     * @param event - The mouse event triggered by clicking the button.
     */
    const handleOpenDetails = useCallback((event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        openModal(node); //Open the modal with the details of the clicked node
    }, [node, openModal]);

	const handleGroupClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		tree.toggleExpanded(node.value);
	}, [tree, node.value]);

    //#endregion

    return (
        <Group
            ref={nodeRef}
            gap={5}
            {...elementProps}
            justify='space-between'
            mt={5}
            onClick={!hasChildren ? handleOpenDetails : handleGroupClick}
            style={groupStyle}
            onMouseEnter={!isHighlighted && !(hasChildren && expanded) ? (e) => {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-gray-0)';
            } : undefined}
            onMouseLeave={!isHighlighted && !(hasChildren && expanded) ? (e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
            } : undefined}
        >
			<Group gap={4} style={{maxWidth: '90%'}}>
				{isTopLevelParent && (
					<Checkbox.Indicator
						checked={checked}
						indeterminate={indeterminate}
						onClick={handleCheckboxClick}
					/>
				)}
				
				{isStatusNode && statusColor && (
					<div 
						style={{
							width: '16px',
							height: '16px',
							backgroundColor: statusColor,
							border: '1px solid #ccc',
							borderRadius: '2px',
							flexShrink: 0
						}}
					/>
				)}

                {hasChildren && <IconChevronDown size={18} style={{ 
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease-in-out'
                }} />}
                <span>{node.label}</span>
            </Group>

            {expandButtonVisible && !hasChildren && (<Group gap={5}>
                <Tooltip label={getTranslation("LABEL_LOCATION_LEAF_OPEN_DETAILS") || "Open details"} position='right'>
                    <ActionIcon 
                        variant='light' 
                        size='md' 
                        color='green'
                        onClick={handleOpenDetails}
                    >
                        <IconArrowsMaximize size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>)}
        </Group>
    );
});

export default LocationLeaf;
